import React from "react";
import { Mail, Phone, MapPin } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const ContactForm = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="18231" className="flex flex-col items-center justify-center h-full min-h-[400px] text-center p-8 animate-in fade-in duration-500">
        <h3 className="text-3xl font-sans font-bold text-primary mb-4">Thank you for reaching out!</h3>
        <p className="text-muted-foreground text-lg mb-8">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg border-primary text-primary hover:bg-accent hover:text-accent-foreground transition-all duration-300">
          Send another request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-sans font-semibold text-primary/80 uppercase tracking-wider">Name</FormLabel>
              <FormControl>
                <Input placeholder="Full Name" {...field} className="border-0 border-b border-input rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-accent bg-transparent placeholder:text-muted-foreground/50 text-base" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-sans font-semibold text-primary/80 uppercase tracking-wider">Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="your@email.com" {...field} className="border-0 border-b border-input rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-accent bg-transparent placeholder:text-muted-foreground/50 text-base" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-sans font-semibold text-primary/80 uppercase tracking-wider">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="0413 771 815" {...field} className="border-0 border-b border-input rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-accent bg-transparent placeholder:text-muted-foreground/50 text-base" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="organization" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-sans font-semibold text-primary/80 uppercase tracking-wider">Property Address / Suburb</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Gorokan, NSW" {...field} className="border-0 border-b border-input rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-accent bg-transparent placeholder:text-muted-foreground/50 text-base" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="subject" rules={{
      required: "Please select a service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-sans font-semibold text-primary/80 uppercase tracking-wider">Service Required</FormLabel>
            <FormControl>
              <select {...field} className="w-full border-0 border-b border-input rounded-none px-0 py-2 shadow-none focus:ring-0 focus:border-accent bg-transparent text-base text-foreground placeholder:text-muted-foreground/50 outline-none cursor-pointer">
                <option value="" disabled>Select a service</option>
                {["Residential Painting", "Commercial Painting", "Interior Painting", "Exterior Painting", "Preparation & Finishing", "Other"].map(option => <option key={option} value={option}>
                    {option}
                  </option>)}
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-sans font-semibold text-primary/80 uppercase tracking-wider">Project Details</FormLabel>
            <FormControl>
              <textarea placeholder="Tell us about your painting project or specific requirements..." {...field} rows={4} className="w-full border-0 border-b border-input rounded-none px-0 py-2 shadow-none focus:ring-0 focus:border-accent bg-transparent text-base resize-none placeholder:text-muted-foreground/50 outline-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-lg">
          {submitError}
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto rounded-lg bg-primary hover:bg-accent hover:text-accent-foreground text-primary-foreground px-10 py-7 text-lg font-semibold uppercase tracking-widest transition-all duration-300 shadow-lg hover:shadow-xl">
          {isSubmitting ? "Sending..." : "Send Quote Request"}
        </Button>
      </div>
    </Form>;
};
export default function Yhteystiedot() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="contact-quote-request" className="bg-background text-foreground py-20 lg:py-32 overflow-hidden">
      <div ref={ref} className={`container mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ease-out transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="flex flex-col lg:flex-row shadow-xl rounded-xl overflow-hidden min-h-[800px]">
          {}
          <div className="w-full lg:w-1/2 bg-primary text-primary-foreground p-12 lg:p-16 flex flex-col justify-between relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-sans font-bold mb-8 leading-tight tracking-tight">
                Get Your Free Quote
              </h2>
              <p className="text-primary-foreground/80 text-lg mb-12 max-w-md font-sans font-normal leading-relaxed">
                With over 30 years of local expertise, Clint's Coastal Painting delivers the perfect finish for homes and businesses across the Northern Central Coast.
              </p>
              
              <div className="space-y-6 font-sans">
                <div className="flex items-start gap-4 group">
                  <div className="p-3 bg-accent/20 rounded-lg group-hover:bg-accent transition-all duration-300">
                    <Mail className="w-6 h-6 text-accent group-hover:text-accent-foreground" />
                  </div>
                  <div>
                    <p className="text-xs text-primary-foreground/60 uppercase tracking-widest mb-1 font-semibold">Email Us</p>
                    <Link to="mailto:Folppclint430@gmail.com" className="text-xl hover:text-accent transition-colors font-medium">
                      Folppclint430@gmail.com
                    </Link>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="p-3 bg-accent/20 rounded-lg group-hover:bg-accent transition-all duration-300">
                    <Phone className="w-6 h-6 text-accent group-hover:text-accent-foreground" />
                  </div>
                  <div>
                    <p className="text-xs text-primary-foreground/60 uppercase tracking-widest mb-1 font-semibold">Call Clint</p>
                    <Link to="tel:0413771815" className="text-xl hover:text-accent transition-colors font-medium">
                      0413 771 815
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {}
            <div className="mt-16 relative z-10">
              <div className="relative aspect-[4/3] w-full max-w-sm overflow-hidden rounded-lg shadow-2xl contrast-110 opacity-95">
                 
                 
              </div>
              <div className="mt-6">
                <p className="font-sans font-bold text-2xl">Clint Folpp</p>
                <p className="text-accent text-sm uppercase tracking-widest font-semibold">Owner & Master Painter</p>
              </div>
            </div>

            {}
            <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-chart-2/10 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3 pointer-events-none"></div>
          </div>

          {}
          <div className="w-full lg:w-1/2 bg-card p-12 lg:p-16 flex flex-col justify-center">
            <div className="max-w-lg w-full mx-auto">
              <FormProvider formKey="contact_quote_form_services" formId="contact_quote_form_services" sectionName="yhteystiedot" defaultValues={{
              name: "",
              email: "",
              phone: "",
              organization: "",
              subject: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              subject: {
                required: true
              },
              message: {
                required: true
              }
            }} submitText="Send Quote Request" successMessage="Thanks for your request! Clint will get back to you as soon as possible with your quote." errorMessage="There was an error sending your request. Please try again or call us directly." className="w-full">
                <ContactForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}